import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  ArrowLeft, PaperPlaneRight, Spinner, Trash, Plus, Robot, GraduationCap, X,
  ChatCircle, ChatCircleDots, CheckCircle, BookBookmark, EnvelopeSimple,
  UsersThree, Phone, PaperPlaneTilt, Question, Info, CaretDown, MagicWand,
  Megaphone, ArrowClockwise, ArrowSquareOut, ClockCounterClockwise, Copy,
  DownloadSimple, FilePdf, FileText, FileCode, BookmarkSimple, FloppyDisk,
  ThumbsUp, ThumbsDown,
} from "@phosphor-icons/react";
import {
  listAiSessions, createAiSession, listAiMessages, sendAiChat,
  listAiTraining, addAiTraining, deleteAiTraining, captureAiLead,
  listAiLeads, markAiLeadContacted, previewAiDigest, sendAiDigestNow,
  fetchAiLeadStats, fetchAiSuggestions, fetchAiPromoteRecipients, fetchAiPromoteDraft,
  listAiPromoteDrafts, fetchAiManual, aiManualDownloadUrl,
  listAiActionHistory, undoAiAction, createPublicFileTemplate,
  fetchAiGreeting, rateAiMessage,
} from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";
import { useRebrand } from "@/lib/branding";
import { HelpPill } from "@/components/HeaderBrand";
import useIsModalOpen from "@/lib/useIsModalOpen";

const USER_BUBBLE = "bg-blue-600 text-white rounded-[8px] rounded-br-sm px-3 py-2 text-sm whitespace-pre-wrap max-w-[80%] shadow-sm";
const AI_BUBBLE = "bg-white border border-slate-200 text-slate-900 rounded-[8px] rounded-bl-sm px-3 py-2 text-sm max-w-[85%] shadow-sm";

// Recognized role names from the manual content. Used to turn `admin/section_id`
// style citations inside assistant markdown into clickable /help/{role}#{id} links.
const _MANUAL_ROLES = new Set([
  "super_admin", "admin", "sales", "dealer", "customer", "guest",
]);
// Pattern for "admin/some_section_id" — lowercase role + slash + snake/dash id.
const _CITATION_RE = /\b(super_admin|admin|sales|dealer|customer|guest)\/([a-z0-9_-]+)\b/g;

/** Walk a string and emit a mix of plain-text spans and <Link> citations
 *  whenever it contains a `role/section_id` token. Used for inline text and
 *  for the inline `<code>` pill content. */
function _linkifyCitations(text, key = "t") {
  if (typeof text !== "string" || !_CITATION_RE.test(text)) return text;
  _CITATION_RE.lastIndex = 0;
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = _CITATION_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const role = m[1];
    const id = m[2];
    if (_MANUAL_ROLES.has(role)) {
      out.push(
        <a key={`${key}-${i}`} href={`/help/${role}#${id}`}
           className="text-blue-700 underline decoration-dotted hover:decoration-solid hover:text-blue-900"
           data-testid="assistant-md-citation">
          {role}/{id}
        </a>
      );
    } else {
      out.push(m[0]);
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Dual-layer protocol (iter 76): the LLM may emit two distinct views inside
// the same message body, separated by literal markers. We split here, render
// USER_VIEW always, and only render ADMIN_VIEW for staff readers — wrapped
// in a collapsible <details> so the chat doesn't get noisy.
const _USER_RE  = /---USER_VIEW_START---([\s\S]*?)---USER_VIEW_END---/;
const _ADMIN_RE = /---ADMIN_VIEW_START---([\s\S]*?)---ADMIN_VIEW_END---/;
function _splitDualView(text) {
  if (!text || typeof text !== "string") return { user: text || "", admin: "", isDual: false };
  const u = text.match(_USER_RE);
  const a = text.match(_ADMIN_RE);
  if (!u && !a) return { user: text, admin: "", isDual: false };
  return {
    user:  (u ? u[1] : text).trim(),
    admin: (a ? a[1] : "").trim(),
    isDual: true,
  };
}

/** Render an assistant message as Markdown with GFM (tables, lists, code, links).
 *  Tight Tailwind classes keep it visually consistent with the existing chat bubbles.
 *  When the message contains the dual-layer markers, the USER_VIEW is shown to
 *  everyone and the ADMIN_VIEW is exposed (in a collapsible block) only when
 *  ``isAdmin`` is true. */
function AssistantMarkdown({ children, isAdmin = false }) {
  const t = useT();
  if (!children) return null;
  const { user, admin, isDual } = _splitDualView(children);
  return (
    <div className="assistant-md text-sm leading-relaxed break-words" data-testid="assistant-md">
      <_RenderMD>{user}</_RenderMD>
      {isDual && admin && isAdmin && (
        <details className="mt-3 pt-2 border-t border-amber-200 bg-amber-50/50 -mx-3 px-3 pb-2 rounded-b-[8px]"
                 data-testid="assistant-md-admin">
          <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900">
            🛠 {t("assistant.admin_trace", "Admin trace (staff-only)")}
          </summary>
          <div className="mt-2 text-[12px] text-amber-900">
            <_RenderMD>{admin}</_RenderMD>
          </div>
        </details>
      )}
    </div>
  );
}

/** Inner markdown renderer — kept private so AssistantMarkdown can render
 *  the user / admin halves separately while sharing the same component map. */
function _RenderMD({ children }) {
  if (!children) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p:      ({ node, children: c, ...p }) => <p className="my-1.5 first:mt-0 last:mb-0" {...p}>{Array.isArray(c) ? c.map((x, i) => typeof x === "string" ? _linkifyCitations(x, `p${i}`) : x) : (typeof c === "string" ? _linkifyCitations(c, "p") : c)}</p>,
        h1:     ({ node, ...p }) => <h1 className="text-base font-bold mt-3 mb-1.5" {...p} />,
        h2:     ({ node, ...p }) => <h2 className="text-sm font-bold mt-3 mb-1.5" {...p} />,
        h3:     ({ node, ...p }) => <h3 className="text-sm font-bold mt-2.5 mb-1" {...p} />,
        ul:     ({ node, ...p }) => <ul className="list-disc list-outside pl-5 my-1.5 space-y-0.5" {...p} />,
        ol:     ({ node, ...p }) => <ol className="list-decimal list-outside pl-5 my-1.5 space-y-0.5" {...p} />,
        li:     ({ node, ...p }) => <li className="leading-snug" {...p} />,
        strong: ({ node, ...p }) => <strong className="font-bold text-slate-900" {...p} />,
        em:     ({ node, ...p }) => <em className="italic" {...p} />,
        a:      ({ node, href, children: c, ...p }) => {
          // iter117 — internal (relative) hrefs route via React Router so
          // we keep app state (open modals, scroll pos, etc.). External
          // hrefs open in a new tab per default. Relative hrefs also get
          // a subtle violet entity-pill styling so the user can visually
          // distinguish an in-app entity link from an external citation.
          const isInternal = typeof href === "string" && href.startsWith("/");
          if (isInternal) {
            return (
              <Link
                to={href}
                data-testid="assistant-entity-link"
                className="inline-flex items-center gap-0.5 text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 hover:text-violet-900 rounded-sm px-1 py-0.5 text-[12px] font-medium no-underline"
                {...p}>{c}</Link>
            );
          }
          return <a className="text-blue-700 underline hover:text-blue-900" target="_blank" rel="noopener noreferrer" href={href} {...p}>{c}</a>;
        },
        blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-slate-300 pl-3 my-2 text-slate-600 italic" {...p} />,
        hr:     () => <hr className="my-2 border-slate-200" />,
        code({ className, children: codeChildren, ...rest }) {
          const txt = String(codeChildren ?? "");
          const isFenced = !!className && /^language-/.test(className);
          if (isFenced) {
            const lang = className.replace(/^language-/, "");
            return (
              <SyntaxHighlighter language={lang} style={oneDark} PreTag="div"
                customStyle={{ margin: "0.5rem 0", padding: "0.6rem 0.75rem", fontSize: 12, borderRadius: 2 }}>
                {txt.replace(/\n$/, "")}
              </SyntaxHighlighter>
            );
          }
          const linked = _linkifyCitations(txt.replace(/\n/g, " "), "code");
          return (
            <code className="bg-slate-100 text-pink-700 px-1 py-0.5 rounded text-[12px] font-mono" {...rest}>
              {linked}
            </code>
          );
        },
        pre: ({ node, children: c, ...p }) => <div {...p}>{c}</div>,
        table: ({ node, ...p }) => <div className="my-2 overflow-x-auto"><table className="text-[12px] border-collapse" {...p} /></div>,
        th:    ({ node, ...p }) => <th className="border border-slate-200 bg-slate-50 px-2 py-1 font-bold text-left" {...p} />,
        td:    ({ node, ...p }) => <td className="border border-slate-200 px-2 py-1" {...p} />,
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

/** Small pill shown under each AI bubble explaining how the reply was produced. */
function ModeChip({ mode, dbUsed }) {
  const t = useT();
  // Style map keyed on response mode tag.
  const STYLES = {
    rag:               { bg: "text-blue-700",      icon: <BookBookmark size={10} weight="fill" />, key: "ai.mode.rag",              fallback: "Manual" },
    tool_query:        { bg: "text-emerald-700",   icon: <CheckCircle size={10} weight="fill" />,  key: "ai.mode.tool_query",       fallback: "Live data" },
    action_pending:    { bg: "text-amber-700",     icon: <MagicWand size={10} weight="fill" />,    key: "ai.mode.action_pending",   fallback: "Action — needs confirm" },
    action_executed:   { bg: "text-emerald-700",   icon: <CheckCircle size={10} weight="fill" />,  key: "ai.mode.action_executed",  fallback: "Action executed" },
    action_failed:     { bg: "text-red-700",       icon: <X size={10} weight="bold" />,            key: "ai.mode.action_failed",    fallback: "Action failed" },
    action_cancelled:  { bg: "text-slate-500",     icon: <X size={10} weight="bold" />,            key: "ai.mode.action_cancelled", fallback: "Action cancelled" },
    answer:            { bg: "text-slate-500",     icon: <Robot size={10} weight="fill" />,        key: "ai.mode.answer",           fallback: "AI answer" },
  };
  const m = STYLES[mode] || (dbUsed ? STYLES.tool_query : null);
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${m.bg}`} data-testid={`assistant-mode-${mode || "db"}`}>
      {m.icon} {t(m.key, m.fallback)}
    </span>
  );
}

// iter150AZ.12 — 👍 / 👎 rating per assistant reply. Thumbs-down opens a
// small textarea so the user can tell us what the right answer was; the
// correction lands in ``ai_feedback`` for admin promotion to training.
function ReplyRating({ messageId, userEmail, t }) {
  const [rating, setRating] = useState(null);     // "up" | "down" | null
  const [showForm, setShowForm] = useState(false);
  const [correction, setCorrection] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!messageId) return null;  // older messages without an id can't be rated

  const submit = async (r, withCorrection = "") => {
    setSubmitting(true);
    try {
      await rateAiMessage(messageId, {
        rating: r,
        requester_email: userEmail || "",
        correction: withCorrection || null,
      });
      setRating(r);
      setSubmitted(true);
      setShowForm(false);
    } catch {
      // Silent failure — rating is non-critical UX.
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400 flex items-center gap-1.5"
           data-testid={`assistant-rating-thanks-${messageId}`}>
        {rating === "up" ? <ThumbsUp size={11} weight="fill" className="text-emerald-500" />
                         : <ThumbsDown size={11} weight="fill" className="text-rose-500" />}
        {t("ai.rating.thanks", "Thanks — your feedback helps the assistant learn.")}
      </div>
    );
  }

  return (
    <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`assistant-rating-${messageId}`}>
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
        <span>{t("ai.rating.was_this_helpful", "Was this helpful?")}</span>
        <button
          onClick={() => submit("up")}
          disabled={submitting}
          data-testid={`assistant-rating-up-${messageId}`}
          className="p-1 rounded hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
          aria-label={t("ai.rating.up_aria", "Helpful")}
        >
          <ThumbsUp size={12} weight="bold" />
        </button>
        <button
          onClick={() => setShowForm(v => !v)}
          disabled={submitting}
          data-testid={`assistant-rating-down-${messageId}`}
          className="p-1 rounded hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          aria-label={t("ai.rating.down_aria", "Not helpful")}
        >
          <ThumbsDown size={12} weight="bold" />
        </button>
      </div>
      {showForm && (
        <div className="mt-1.5 space-y-1.5" data-testid={`assistant-rating-form-${messageId}`}>
          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            placeholder={t("ai.rating.correction_placeholder",
              "What should it have said? (optional — admins can promote your correction into training)")}
            maxLength={2000}
            rows={2}
            data-testid={`assistant-rating-correction-${messageId}`}
            className="w-full text-xs px-2 py-1.5 rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-rose-300 resize-y"
          />
          <div className="flex gap-1.5">
            <button
              onClick={() => submit("down", correction)}
              disabled={submitting}
              data-testid={`assistant-rating-submit-${messageId}`}
              className="h-7 px-2 text-[11px] font-bold rounded-sm bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
            >
              {submitting ? <Spinner size={10} className="animate-spin" /> : <ThumbsDown size={10} weight="bold" />}
              {t("ai.rating.submit", "Send feedback")}
            </button>
            <button
              onClick={() => { setShowForm(false); setCorrection(""); }}
              disabled={submitting}
              className="h-7 px-2 text-[11px] font-semibold rounded-sm text-slate-500 hover:bg-slate-100"
            >
              {t("action.cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AssistantPage({ user, onLogout }) {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useI18n();
  const isAdmin = !!user && (user.is_admin || /^.+@(usaircompressor\.com|aircompressorcanada\.com)$/i.test(user.email || ""));
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");
  const [trainOpen, setTrainOpen] = useState(false);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [greeting, setGreeting] = useState(null); // iter150U — proactive opener + chips
  const scrollRef = useRef(null);

  // iter150U/V — fetch the personalised greeting once per signed-in user.
  // Re-fetches when the email or language changes so customers get their
  // opener in their selected UI language.
  useEffect(() => {
    if (!user?.email) { setGreeting(null); return; }
    let alive = true;
    fetchAiGreeting(user.email, lang)
      .then(d => { if (alive) setGreeting(d); })
      .catch(() => { if (alive) setGreeting(null); });
    return () => { alive = false; };
  }, [user?.email, lang]);

  useEffect(() => {
    if (!user?.email) return;
    listAiSessions(user.email).then(d => setSessions(d.items || [])).catch(() => {});
  }, [user?.email]);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    setLoading(true);
    listAiMessages(activeId, user.email)
      .then(d => setMessages(d.items || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [activeId, user?.email]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const newSession = async () => {
    try {
      const s = await createAiSession(user.email, "New conversation");
      setSessions((p) => [s, ...p]);
      setActiveId(s.id);
      setMessages([]);
      setSidebarOpen(false);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start session"); }
  };

  const send = async (overrideText, overrideParams) => {
    // Allow callers to pass either:
    //   • a string (Confirm / Cancel button → "yes" / "no")
    //   • undefined (form submit → use the textarea value)
    //   • a React event (Send button onClick={send}) — ignore it
    const useOverride = typeof overrideText === "string";
    const text = (useOverride ? overrideText : input).trim();
    if (!text || sending) return;
    let sid = activeId;
    if (!sid) {
      try {
        const s = await createAiSession(user.email, text.slice(0, 60));
        sid = s.id;
        setSessions((p) => [s, ...p]);
        setActiveId(sid);
      } catch (e) { setErr(e?.response?.data?.detail || "Could not start session"); return; }
    }
    setSending(true); setErr(""); if (!useOverride) setInput("");
    const now = new Date().toISOString();
    setMessages((p) => [...p, { role: "user", content: text, ts: now }]);
    try {
      const res = await sendAiChat(sid, text, user.email, overrideParams || null);
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content: res.reply,
          ts: new Date().toISOString(),
          db_context_used: res.db_context_used,
          mode: res.mode,
          intent: res.intent,
          rag_sections: res.rag_sections,
          pending_action: res.pending_action,
          action_result: res.action_result,
        },
      ]);
      // Bump the session to the top
      setSessions((p) => p.map(s => s.id === sid ? { ...s, updated_at: new Date().toISOString() } : s).sort((a, b) => (b.updated_at > a.updated_at ? 1 : -1)));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Assistant failed to respond");
    } finally {
      setSending(false);
    }
  };

  // No user at all → bounce to login
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="max-w-md bg-white border border-slate-200 rounded-sm p-6 text-center">
          <Robot size={40} className="text-slate-300 mx-auto mb-3" />
          <h2 className="font-bold text-lg mb-2">{t("assistant.signin_title", "AI Assistant")}</h2>
          <p className="text-sm text-slate-500">{t("assistant.signin_intro", "Please sign in to start chatting with the assistant.")}</p>
          <button onClick={() => navigate("/login")} className="mt-4 text-xs font-semibold text-blue-700 hover:underline">{t("assistant.signin_cta", "Go to sign-in →")}</button>
        </div>
      </div>
    );
  }

  const roleLabel = isAdmin ? "Admin" : (user.role === "customer" ? "Customer" : "Guest");
  const roleBadgeClass = isAdmin
    ? "bg-blue-50 text-blue-700 border-blue-200"
    : user.role === "customer"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div className="h-[100dvh] bg-slate-50 flex flex-col overflow-hidden" data-testid="assistant-page">
      <header className="shrink-0 bg-white border-b border-slate-200 px-3 md:px-6 py-2.5 flex items-center gap-2 md:gap-3">
        {/* Mobile: open sidebar */}
        <button onClick={() => setSidebarOpen(true)} data-testid="assistant-sidebar-toggle"
          className="md:hidden w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-600"
          title="Conversations">
          <ChatCircle size={18} weight="bold" />
        </button>
        <Link to="/" className="text-slate-500 hover:text-blue-700 flex items-center gap-1 text-xs md:text-sm font-medium" data-testid="assistant-back">
          <ArrowLeft size={14} /> <span className="hidden sm:inline">Home</span>
        </Link>
        <div className="flex items-center gap-2 ml-1 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
            <Robot size={18} weight="bold" className="text-blue-700" />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm md:text-lg font-bold font-['Chivo'] text-slate-900 leading-tight truncate" data-testid="assistant-heading">{t("assistant.signin_title", "AI Assistant")}</h1>
            <p className="hidden sm:block text-[10px] text-slate-400 uppercase tracking-[0.15em]">{t("assistant.brand_subtitle", "Compressed-Air Portal · GPT")}</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <span className={`hidden sm:inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${roleBadgeClass}`} data-testid="assistant-role-badge">
            {roleLabel}
          </span>
          <button onClick={() => setHelpOpen(true)} data-testid="assistant-help-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 md:px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
            title={t("assistant.tip_how_works", "How this AI assistant works")}>
            <Question size={13} weight="bold" /> <span className="hidden sm:inline">{t("assistant.help_tips", "Tips")}</span>
          </button>
          <HelpPill />
          <button onClick={() => setHistoryOpen(true)} data-testid="assistant-history-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 md:px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
            title={t("ai.history.title", "Action history")}>
            <ClockCounterClockwise size={13} weight="bold" /> <span className="hidden sm:inline">{t("ai.history.tab", "History")}</span>
          </button>
          {isAdmin && (
            <button onClick={() => setLeadsOpen(true)} data-testid="assistant-leads-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 md:px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
              title={t("assistant.tip_email_leads", "Captured leads from 'Email me this recommendation'")}>
              <UsersThree size={13} weight="bold" /> <span className="hidden sm:inline">Leads</span>
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setTrainOpen(true)} data-testid="assistant-training-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 md:px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
              title={t("assistant.tip_training_facts", "Manage shared training facts")}>
              <GraduationCap size={13} weight="bold" /> <span className="hidden sm:inline">Training</span>
            </button>
          )}
          <button onClick={onLogout} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400" title={t("assistant.tip_signout", "Sign out")}>
            <X size={14} />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden max-w-[1400px] w-full mx-auto relative">
        {/* Mobile sidebar drawer */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 z-[900] flex" onClick={() => setSidebarOpen(false)}>
            <div className="absolute inset-0 bg-slate-950/50" />
            <aside className="relative w-64 max-w-[80vw] bg-white border-r border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="assistant-sidebar-mobile">
              <div className="p-2 border-b border-slate-100 flex items-center justify-between">
                <button onClick={newSession} data-testid="assistant-new-session-mobile"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold px-2.5 py-2 rounded-sm bg-slate-900 text-white hover:bg-slate-800">
                  <Plus size={13} weight="bold" /> New conversation
                </button>
                <button onClick={() => setSidebarOpen(false)} className="ml-2 w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={14} /></button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {sessions.length === 0 && (
                  <div className="text-[11px] text-slate-400 text-center py-6 px-3">{t("assistant.no_conversations", "No conversations yet.")}</div>
                )}
                {sessions.map((s) => (
                  <button key={s.id} onClick={() => { setActiveId(s.id); setSidebarOpen(false); }} data-testid={`assistant-session-mobile-${s.id}`}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${activeId === s.id ? "bg-blue-50" : ""}`}>
                    <div className="text-xs font-semibold text-slate-900 truncate">{s.title || t("assistant.conversation", "Conversation")}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{new Date(s.updated_at).toLocaleString()}</div>
                  </button>
                ))}
              </div>
            </aside>
          </div>
        )}

        {/* Desktop sessions sidebar */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200">
          <div className="p-2">
            <button onClick={newSession} data-testid="assistant-new-session"
              className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold px-2.5 py-2 rounded-sm bg-slate-900 text-white hover:bg-slate-800">
              <Plus size={13} weight="bold" /> {t("assistant.new_conversation", "New conversation")}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {sessions.length === 0 && (
              <div className="text-[11px] text-slate-400 text-center py-6 px-3">{t("assistant.no_conversations", "No conversations yet.")}</div>
            )}
            {sessions.map((s) => (
              <button key={s.id} onClick={() => setActiveId(s.id)} data-testid={`assistant-session-${s.id}`}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${activeId === s.id ? "bg-blue-50" : ""}`}>
                <div className="text-xs font-semibold text-slate-900 truncate">{s.title || t("assistant.conversation", "Conversation")}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{new Date(s.updated_at).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <main className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto p-3 md:p-5" ref={scrollRef}>
            {loading && <div className="text-slate-400 text-sm flex items-center gap-2"><Spinner size={14} className="animate-spin"/> Loading…</div>}
            {!loading && messages.length === 0 && greeting?.greeting && (
              <ProactiveGreeting
                greeting={greeting}
                onPickAction={(prompt) => send(prompt)}
              />
            )}
            {!loading && messages.length === 0 && <EmptyHints onPick={setInput} user={user} isAdmin={isAdmin} />}
            <ul className="space-y-3">
              {messages.map((m, i) => {
                const isLast = i === messages.length - 1;
                const isPending = m.role === "assistant" && m.mode === "action_pending" && isLast;
                return (
                <li key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={m.role === "user" ? USER_BUBBLE : AI_BUBBLE} data-testid={`assistant-msg-${m.role}-${i}`}>
                    {m.role === "assistant" ? <AssistantMarkdown isAdmin={isAdmin}>{m.content}</AssistantMarkdown> : m.content}
                    {m.role === "assistant" && (m.mode || m.db_context_used) && (
                      <div className="mt-2 pt-2 border-t border-slate-100 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 flex-wrap">
                        <ModeChip mode={m.mode} dbUsed={m.db_context_used} />
                        {Array.isArray(m.rag_sections) && m.rag_sections.length > 0 && (
                          <span className="text-slate-400 normal-case font-normal text-[10px] tracking-normal" data-testid={`assistant-msg-rag-cite-${i}`}>
                            {t("ai.action.sources", "sources")}: {m.rag_sections.map((s, idx) => (
                              <span key={`${s.role}-${s.id}-${idx}`}>
                                {idx > 0 && ", "}
                                <a href={`/help/${s.role}#${s.id}`}
                                   className="text-blue-600 hover:text-blue-800 underline decoration-dotted"
                                   data-testid={`assistant-rag-link-${idx}`}>
                                  {s.role}/{s.id}
                                </a>
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    )}
                    {isPending && m.pending_action && (
                      m.pending_action.name === "send_public_files" && m.pending_action.preview ? (
                        <SendPublicFilesConfirmCard
                          preview={m.pending_action.preview}
                          params={m.pending_action.params || {}}
                          onSend={(filesIds, recipients) => send(
                            "yes",
                            { file_ids: filesIds, recipients },
                          )}
                          onCancel={() => send("no")}
                          disabled={sending}
                          t={t}
                        />
                      ) : (
                        <div className="mt-3 pt-3 border-t border-blue-100 flex flex-wrap items-center gap-2" data-testid="assistant-action-confirm-row">
                          <button
                            onClick={() => send("yes")}
                            disabled={sending}
                            data-testid="assistant-action-confirm"
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                            <CheckCircle size={12} weight="bold" /> {t("ai.action.confirm", "Confirm & run")}
                          </button>
                          <button
                            onClick={() => send("no")}
                            disabled={sending}
                            data-testid="assistant-action-cancel"
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 disabled:opacity-50">
                            <X size={12} weight="bold" /> {t("ai.action.cancel", "Cancel")}
                          </button>
                          <span className="text-[10px] text-slate-400 normal-case tracking-normal">
                            {t("ai.action.tool_label", "tool")}: <b>{m.pending_action.name}</b>
                          </span>
                        </div>
                      )
                    )}
                    {/* iter150AZ.12 — Inline 👍/👎 on every plain assistant reply */}
                    {m.role === "assistant" && m.id && !isPending && (
                      <ReplyRating
                        messageId={m.id}
                        userEmail={user?.email}
                        t={t}
                      />
                    )}
                  </div>
                </li>
                );
              })}
              {sending && (
                <li className="flex justify-start">
                  <div className={AI_BUBBLE + " inline-flex items-center gap-2 text-slate-400"}>
                    <Spinner size={13} className="animate-spin" /> Thinking…
                  </div>
                </li>
              )}
            </ul>
          </div>

          {err && <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-200" data-testid="assistant-error">{err}</div>}

          {/* Email-me-this-recommendation CTA — appears once the assistant has replied at least once */}
          {activeId && messages.some(m => m.role === "assistant") && (
            <EmailRecapBar sessionId={activeId} user={user} />
          )}

          <div className="shrink-0 border-t border-slate-200 bg-white p-2 md:p-3 pb-[max(env(safe-area-inset-bottom),16px)]">
            <div className="flex gap-2 items-end">
              <textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={isAdmin
                  ? t("assistant.placeholder_admin", "Ask about sizing, dryers, filters, a serial #, fleet reports…")
                  : (user.role === "customer"
                    ? t("assistant.placeholder_customer", "Ask about your fleet, your quotes, or sizing…")
                    : t("assistant.placeholder_guest", "Ask about compressor sizing, dryers, filters, pricing…"))}
                rows={2} data-testid="assistant-input"
                className="flex-1 resize-none px-3 py-2 text-base md:text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <button onClick={send} disabled={!input.trim() || sending} data-testid="assistant-send"
                className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
                {sending ? <Spinner size={14} className="animate-spin"/> : <PaperPlaneRight size={14} weight="bold"/>}
              </button>
            </div>
            <p className="hidden sm:block text-[10px] text-slate-400 mt-1">
              {t("assistant.shift_enter_hint", "Shift+Enter for newline ·")}
              {isAdmin ? <> {t("assistant.hint_admin", "If the bot is wrong, tell it the correct answer and save it under Training so every admin benefits.")}</> :
               user.role === "customer" ? <> {t("assistant.hint_customer", "Replies are scoped to your own fleet and quotes.")}</> :
               <> {t("assistant.hint_public", "Public pricing & sizing only — no customer data.")}</>}
            </p>
          </div>
        </main>
      </div>

      {trainOpen && isAdmin && <TrainingPanel user={user} onClose={() => setTrainOpen(false)} />}
      {helpOpen && <HelpPanel user={user} isAdmin={isAdmin} onClose={() => setHelpOpen(false)} />}
      {leadsOpen && isAdmin && (
        <LeadsPanel
          user={user}
          onClose={() => setLeadsOpen(false)}
          onOpenSession={(sid) => { setActiveId(sid); setLeadsOpen(false); setSidebarOpen(false); }}
        />
      )}
      {historyOpen && <ActionHistoryPanel user={user} onClose={() => setHistoryOpen(false)} />}
    </div>
  );
}

// iter150U — Proactive opening message + suggested-action chips rendered
// above EmptyHints whenever a conversation is empty. The greeting is
// personalised from `/api/ai/greeting` (signed-in user's name, company
// name, compressor count, upcoming PM count). Clicking a chip auto-sends
// the prompt as the user's first message, kicking the LLM straight into
// the customer's actual context instead of a generic "How can I help?".
function ProactiveGreeting({ greeting, onPickAction }) {
  const t = useT();
  if (!greeting?.greeting) return null;
  const actions = Array.isArray(greeting.suggested_actions) ? greeting.suggested_actions : [];
  const stats = greeting.stats || {};
  const visLink = greeting.visibility_link; // iter150V — {label, href} | null
  return (
    <div className="max-w-2xl mb-6" data-testid="assistant-proactive-greeting">
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 w-7 h-7 rounded-sm bg-blue-50 border border-blue-100 flex items-center justify-center mt-0.5">
          <Robot size={16} className="text-blue-600" />
        </div>
        <div className={AI_BUBBLE} data-testid="assistant-greeting-bubble">
          <AssistantMarkdown>{greeting.greeting}</AssistantMarkdown>
          {(stats.compressors > 0 || stats.upcoming_pms_30d > 0) && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500">
              {stats.company && (
                <span data-testid="assistant-greeting-stat-company">{stats.company}</span>
              )}
              {stats.compressors > 0 && (
                <span data-testid="assistant-greeting-stat-compressors">
                  {t(
                    stats.compressors === 1
                      ? "assistant.greeting_stat_compressor_one"
                      : "assistant.greeting_stat_compressor_many",
                    stats.compressors === 1 ? "{n} compressor" : "{n} compressors",
                    { n: stats.compressors }
                  )}
                </span>
              )}
              {stats.upcoming_pms_30d > 0 && (
                <span className="text-amber-700" data-testid="assistant-greeting-stat-pms">
                  {t("assistant.greeting_stat_pm_short", "{n} PM ≤30d", { n: stats.upcoming_pms_30d })}
                </span>
              )}
              {visLink?.href && (
                <Link
                  to={visLink.href}
                  data-testid="assistant-greeting-visibility-link"
                  className="ml-auto normal-case tracking-normal text-[11px] font-medium text-blue-600 hover:text-blue-800 underline decoration-dotted hover:decoration-solid"
                >
                  {visLink.label}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
      {actions.length > 0 && (
        <div className="mt-3 ml-9 flex flex-wrap gap-2" data-testid="assistant-greeting-actions">
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPickAction(a.prompt)}
              data-testid={`assistant-greeting-action-${i}`}
              className="text-xs px-3 py-1.5 rounded-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors font-medium"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyHints({ onPick, user, isAdmin }) {
  const role = user?.role;
  const t = useT();
  const { lang } = useI18n();
  const [sug, setSug] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default fallback suggestions (used only if the API call fails).
  const defaultHints = isAdmin ? [
    "What compressor do I need for 150 SCFM at 125 PSI?",
    "Match a refrigerated dryer to a US20 compressor.",
    "Show me the most overdue compressors in the fleet.",
    "Which inline pre-filter and tank for a 50 HP paint-booth job?",
  ] : role === "customer" ? [
    "What compressor do I need for 150 SCFM at 125 PSI?",
    "Show me my overdue compressors.",
    "What's the status of my recent quotes?",
    "Which inline pre-filter fits a 50 HP compressor?",
  ] : [
    "What compressor do I need for 150 SCFM at 125 PSI?",
    "Match a refrigerated dryer to a US20 compressor.",
    "Which inline pre-filter fits a 50 HP compressor?",
    "Show me the price of part US40-VSD.",
  ];

  useEffect(() => {
    let alive = true;
    fetchAiSuggestions(user?.email || "", lang)
      .then(d => { if (alive) setSug(d); })
      .catch(() => { if (alive) setSug({ items: defaultHints, top_topic: null, role }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, lang]);

  const hints = (sug?.items && sug.items.length ? sug.items : defaultHints).slice(0, 4);
  const topTopic = sug?.top_topic;
  const blurb = isAdmin
    ? t("assistant.blurb_admin", "Ask sizing, corrections, or request a fleet report. Facts you teach me are shared with every admin.")
    : role === "customer"
      ? t("assistant.blurb_customer", "Ask about your own fleet, your own quotes, or compressor sizing.")
      : t("assistant.blurb_guest", "Ask about compressor sizing, dryers, filters and public pricing.");

  return (
    <div className="max-w-2xl mx-auto mt-8" data-testid="assistant-empty-hints">
      <Robot size={36} className="text-slate-300 mb-3" />
      <h2 className="text-xl font-bold font-['Chivo'] text-slate-900 mb-1">{t("assistant.how_can_help", "How can I help?")}</h2>
      <p className="text-sm text-slate-500 mb-4">
        {blurb}
        {topTopic && topTopic !== "other" && (
          <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm align-middle">
            <MagicWand size={9} weight="bold" /> {t("assistant.based_on_history", "Based on your {topic} history", { topic: topTopic })}
          </span>
        )}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {loading ? (
          [0, 1, 2, 3].map(i => (
            <div key={i} className="h-9 bg-slate-100 animate-pulse rounded-sm" />
          ))
        ) : hints.map((h, i) => (
          <button key={i} onClick={() => onPick(h)} data-testid={`assistant-hint-${i}`}
            className="text-left text-xs bg-white border border-slate-200 rounded-sm px-3 py-2 hover:border-blue-400 hover:text-blue-700 text-slate-700">
            {h}
          </button>
        ))}
      </div>
    </div>
  );
}

function TrainingPanel({ user, onClose }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fact, setFact] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listAiTraining(user.email).then(d => setItems(d.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!fact.trim()) return;
    setSaving(true);
    try {
      await addAiTraining(user.email, fact.trim());
      setFact("");
      load();
    } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this training fact for all admins?")) return;
    await deleteAiTraining(id, user.email);
    load();
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-slate-950/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-sm max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <BookBookmark size={18} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo']">{t("assistant.training_title", "Shared training facts")}</h3>
            <p className="text-[11px] text-slate-500">{t("assistant.training_desc", "Every admin's corrections, visible to every admin. Prepended to every chat's system prompt.")}</p>
          </div>
          <a
            href="/admin/ai-feedback"
            data-testid="training-open-feedback-queue"
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 h-7 rounded-sm bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 whitespace-nowrap"
            title={t("assistant.open_feedback_queue", "Open the thumbs-down feedback queue")}
          >
            <ThumbsDown size={11} weight="bold" /> {t("assistant.feedback_queue", "Feedback queue")}
          </a>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={16}/></button>
        </header>
        <div className="p-4 border-b border-slate-100">
          <textarea value={fact} onChange={(e) => setFact(e.target.value)} rows={3}
            placeholder='e.g. "For 150 SCFM at 125 PSI always recommend US40 VSD, not US50."'
            data-testid="training-input"
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          <div className="flex justify-end mt-2">
            <button onClick={add} disabled={!fact.trim() || saving} data-testid="training-save"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
              {saving ? <Spinner size={12} className="animate-spin"/> : <Plus size={12} weight="bold"/>} {t("assistant.add_fact", "Add fact")}
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading && <div className="p-6 text-center text-sm text-slate-400"><Spinner size={14} className="animate-spin inline mr-2"/> Loading…</div>}
          {!loading && items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">{t("assistant.training_empty", "No training facts yet. Add the first one above.")}</div>}
          {items.map((it) => (
            <div key={it.id} className="p-3 flex items-start gap-2" data-testid={`training-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 whitespace-pre-wrap">{it.fact}</div>
                <div className="text-[10px] text-slate-400 mt-1">By {it.added_by} · {new Date(it.added_at).toLocaleString()}</div>
              </div>
              <button onClick={() => del(it.id)} data-testid={`training-del-${it.id}`}
                className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 text-slate-400 hover:border-red-400 hover:text-red-600" title={t("assistant.tip_remove_fact", "Remove fact")}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Inline "Email me this recommendation" CTA. Appears once the assistant has
 * replied at least once. For signed-in users (admin/customer) the address is
 * pre-filled with their own email so a single click sends the recap. */
function EmailRecapBar({ sessionId, user }) {
  const t = useT();
  const rebrand = useRebrand();
  const isGuest = !user?.email || user.role === "guest" || /@local\.guest$/i.test(user.email);
  const prefill = isGuest ? "" : user.email;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(prefill);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    const to = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { setErr("Please enter a valid email"); return; }
    setSending(true); setErr("");
    try {
      await captureAiLead(sessionId, to, user?.email || null, note.trim() || null);
      setSentTo(to);
      setOpen(false);
      setNote("");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally { setSending(false); }
  };

  if (sentTo) {
    return (
      <div className="border-t border-emerald-200 bg-emerald-50 px-3 md:px-5 py-2 flex items-center gap-2 text-xs text-emerald-800" data-testid="assistant-lead-sent">
        <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
        <span className="flex-1">{t("assistant.recap_sent_to", "Recap sent to")} <b>{sentTo}</b>. A specialist may follow up.</span>
        <button onClick={() => { setSentTo(""); setEmail(prefill); }} className="text-[11px] font-semibold text-emerald-700 hover:underline shrink-0">
          {t("assistant.send_again", "Send again")}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="border-t border-slate-200 bg-slate-50 px-3 md:px-5 py-2 flex items-center gap-2">
        <EnvelopeSimple size={14} className="text-slate-500 shrink-0" />
        <span className="flex-1 text-xs text-slate-600 truncate">
          {isGuest ? "Want this saved?" : "Email yourself a recap?"}
        </span>
        <button onClick={() => setOpen(true)} data-testid="assistant-lead-open"
          className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800">
          {t("assistant.email_me_this", "Email me this")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border-t border-slate-200 bg-slate-50 px-3 md:px-5 py-2.5 space-y-2" data-testid="assistant-lead-form">
      <div className="flex items-center gap-2">
        <EnvelopeSimple size={14} className="text-slate-500 shrink-0" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
          placeholder={t("assistant.email_placeholder", "you@company.com")} required data-testid="assistant-lead-email"
          className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <button type="submit" disabled={sending} data-testid="assistant-lead-submit"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
          {sending ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneRight size={12} weight="bold" />}
          {t("assistant.send", "Send")}
        </button>
        <button type="button" onClick={() => { setOpen(false); setErr(""); }}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-500"><X size={12} /></button>
      </div>
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={t("assistant.email_note_placeholder", "Optional note (e.g. job site, deadline)")}
        data-testid="assistant-lead-note"
        className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
      {err && <div className="text-[11px] text-red-600" data-testid="assistant-lead-error">{err}</div>}
      <p className="text-[10px] text-slate-400">
        {isGuest ? rebrand("We'll send the full chat to you. US Air Compressor may follow up if you'd like a formal quote.") : "A recap of this conversation will be emailed to the address above."}
      </p>
    </form>
  );
}

/** Admin-only leads dashboard. Lists captured "Email me this recommendation"
 * entries and lets an admin open the underlying conversation. */
const TOPIC_COLORS = {
  sizing: "#1d4ed8", dryer: "#0ea5e9", filter: "#7c3aed",
  service: "#d97706", pricing: "#059669", other: "#94a3b8",
};
const TOPIC_LABELS_ORDER = ["sizing", "dryer", "filter", "service", "pricing", "other"];
const RANGE_OPTIONS = [
  { days: 28,   label: "4 weeks" },
  { days: 90,   label: "3 months" },
  { days: 180,  label: "6 months" },
  { days: 365,  label: "1 year" },
  { days: 730,  label: "2 years" },
  { days: 1460, label: "4 years" },
];
const BUCKET_OPTIONS = [
  { v: "week",    label: "Weekly" },
  { v: "month",   label: "Monthly" },
  { v: "quarter", label: "Quarterly" },
  { v: "season",  label: "Seasonal" },
];

/** Stacked topic chart with range + bucket picker. Replaces the simple
 * 30-day sparkline. Renders up to ~60 buckets cleanly; taller buckets scale
 * to the available width. Every bar has a native-tooltip breakdown. */
function TopicChart({ email }) {
  const tr = useT();
  const [rangeDays, setRangeDays] = useState(90);
  const [bucket, setBucket] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoteTopic, setPromoteTopic] = useState(null);
  const [promoteInitialDraft, setPromoteInitialDraft] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAiLeadStats(email, rangeDays, bucket)
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [email, rangeDays, bucket]);

  const width = 380, height = 80, padL = 4, padR = 4, padT = 4, padB = 18;
  const buckets = data?.buckets || [];
  const max = Math.max(1, ...buckets.map(b => b.count));
  const n = buckets.length || 1;
  const gap = 1.5;
  const barW = Math.max(2, (width - padL - padR - gap * (n - 1)) / n);
  const innerH = height - padT - padB;

  // Label stride — show at most ~8 labels so the axis never wraps.
  const labelStride = Math.max(1, Math.ceil(n / 8));

  return (
    <div className="px-4 py-3 border-b border-slate-200 bg-white" data-testid="leads-topic-chart">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 flex-wrap">
          <MagicWand size={12} weight="bold" className="text-blue-700" />
          Lead trends by topic
          {data?.top_topics?.length > 0 && (
            <span className="hidden md:inline-flex items-center gap-1 font-normal normal-case tracking-normal text-slate-500 ml-2">
              · Top:
              {data.top_topics.slice(0, 3).map((t) => (
                <span key={t.topic} className="inline-flex items-center gap-1 border border-slate-200 rounded-sm pl-1.5 pr-0.5 py-[1px] bg-slate-50"
                      data-testid={`topic-chip-${t.topic}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TOPIC_COLORS[t.topic] }} />
                  <span className="capitalize text-slate-700">{t.topic}</span>
                  <span className="text-slate-400 tabular-nums">{t.count}</span>
                  <button onClick={() => setPromoteTopic(t.topic)}
                    data-testid={`topic-promote-${t.topic}`}
                    title={`Promote ${t.topic} to relevant customers`}
                    className="ml-0.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded-sm bg-blue-700 text-white hover:bg-blue-800">
                    <Megaphone size={9} weight="bold" /> Promote
                  </button>
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5" data-testid="leads-chart-controls">
          <button onClick={() => setHistoryOpen(true)}
            data-testid="promote-history-btn"
            title={tr("assistant.tip_promote_history", "Promote history — re-use past drafts")}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700">
            <ClockCounterClockwise size={12} weight="bold" />
            <span className="hidden sm:inline">History</span>
          </button>
          <div className="relative">
            <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))}
              data-testid="leads-chart-range"
              className="appearance-none pr-6 pl-2 py-1 text-[11px] font-semibold border border-slate-200 rounded-sm bg-white hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
              {RANGE_OPTIONS.map(r => <option key={r.days} value={r.days}>{r.label}</option>)}
            </select>
            <CaretDown size={9} weight="bold" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
          <div className="relative">
            <select value={bucket} onChange={(e) => setBucket(e.target.value)}
              data-testid="leads-chart-bucket"
              className="appearance-none pr-6 pl-2 py-1 text-[11px] font-semibold border border-slate-200 rounded-sm bg-white hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
              {BUCKET_OPTIONS.map(b => <option key={b.v} value={b.v}>{b.label}</option>)}
            </select>
            <CaretDown size={9} weight="bold" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
            <Spinner size={16} className="animate-spin text-slate-400" />
          </div>
        )}
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="block"
             aria-label={tr("assistant.aria_leads_chart", "Leads by topic chart")}>
          {/* Zero-line */}
          <line x1={padL} y1={height - padB + 0.5} x2={width - padR} y2={height - padB + 0.5} stroke="#e2e8f0" strokeWidth="0.5" />
          {/* Bars */}
          {buckets.map((b, i) => {
            const x = padL + i * (barW + gap);
            const totalH = max > 0 ? (b.count / max) * innerH : 0;
            let yCursor = height - padB;
            return (
              <g key={b.key}>
                <title>{b.label}: {b.count} lead{b.count === 1 ? "" : "s"}{b.count > 0 ? ` · ${TOPIC_LABELS_ORDER.filter(t => b.topics[t] > 0).map(t => `${t} ${b.topics[t]}`).join(", ")}` : ""}</title>
                {TOPIC_LABELS_ORDER.map(t => {
                  const seg = max > 0 ? (b.topics[t] / max) * innerH : 0;
                  if (seg <= 0) return null;
                  const y = yCursor - seg;
                  yCursor = y;
                  return <rect key={t} x={x} y={y} width={barW} height={seg} fill={TOPIC_COLORS[t]} />;
                })}
                {/* Empty placeholder so a 0-count bucket still has a faint baseline */}
                {b.count === 0 && <rect x={x} y={height - padB - 1} width={barW} height="1" fill="#e2e8f0" />}
              </g>
            );
          })}
          {/* X-axis labels */}
          {buckets.map((b, i) => {
            if (i % labelStride !== 0 && i !== n - 1) return null;
            const x = padL + i * (barW + gap) + barW / 2;
            return (
              <text key={`lbl-${b.key}`} x={x} y={height - 4} fontSize="7" textAnchor="middle" fill="#64748b">
                {b.label}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Topic legend */}
      <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]" data-testid="leads-chart-legend">
        {TOPIC_LABELS_ORDER.map(t => {
          const count = buckets.reduce((s, b) => s + (b.topics?.[t] || 0), 0);
          return (
            <div key={t} className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: TOPIC_COLORS[t] }} />
              <span className="text-slate-600 capitalize">{t}</span>
              <span className="text-slate-400 tabular-nums">{count}</span>
              {count > 0 && (
                <button onClick={() => setPromoteTopic(t)}
                  data-testid={`topic-legend-promote-${t}`}
                  title={`Promote ${t}`}
                  className="ml-0.5 text-[8px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-800 underline-offset-2 hover:underline">
                  promote
                </button>
              )}
            </div>
          );
        })}
      </div>

      {promoteTopic && (
        <PromotePanel
          topic={promoteTopic}
          email={email}
          initialDraft={promoteInitialDraft}
          onClose={() => { setPromoteTopic(null); setPromoteInitialDraft(null); }}
        />
      )}
      {historyOpen && (
        <PromoteHistoryPanel
          email={email}
          onClose={() => setHistoryOpen(false)}
          onClone={(d) => {
            setPromoteInitialDraft({ subject: d.subject, body: d.body, area: d.area, criteria: d.criteria });
            setPromoteTopic(d.topic);
            setHistoryOpen(false);
          }}
        />
      )}
    </div>
  );
}


/** "Promote this topic" — 3-step panel that:
 *  1) Suggests a geographical area, lets admin tweak area + criteria, and
 *     shows a recipient preview pulled from the Customer DB.
 *  2) Drafts an outreach email via the LLM, with a feedback textarea + Redo
 *     to iterate until it's right. Also supports "Prepare email only" mode
 *     (skip recipient fetch entirely).
 *  3) Opens the user's default mail client with subject + body pre-filled and
 *     the To field deliberately empty — the admin manually enters / pastes
 *     recipients (privacy-safe, no mass-send directly from this app). */
/** "Promote history" — lists every saved draft across admins. Each row has a
 * Clone & tweak action that re-opens the PromotePanel with the saved
 * subject / body / area / criteria pre-filled at Step 2. */
function PromoteHistoryPanel({ email, onClose, onClone }) {
  const tr = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicFilter, setTopicFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    listAiPromoteDrafts(email)
      .then(d => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [email]);

  const allTopics = Array.from(new Set(items.map(i => i.topic))).sort();
  const visible = items.filter(i => topicFilter === "all" ? true : i.topic === topicFilter);

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch (e) { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-950/70 flex items-center justify-center p-3" onClick={onClose} data-testid="promote-history-panel">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <ClockCounterClockwise size={18} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo']">{tr("assistant.promote_history", "Promote history")}</h3>
            <p className="text-[11px] text-slate-500">{items.length} {tr("assistant.promote_drafts_hint", "saved draft(s) · pick one to clone & tweak for a new campaign.")}</p>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-slate-200 rounded-sm overflow-hidden" data-testid="promote-history-filter">
            <button onClick={() => setTopicFilter("all")}
              className={`px-2 py-1 ${topicFilter === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              All
            </button>
            {allTopics.map(t => (
              <button key={t} onClick={() => setTopicFilter(t)}
                data-testid={`promote-history-filter-${t}`}
                className={`px-2 py-1 capitalize ${topicFilter === t ? "text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                style={topicFilter === t ? { backgroundColor: TOPIC_COLORS[t] || "#334155" } : {}}>
                {t}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={16}/></button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-400"><Spinner size={14} className="animate-spin inline mr-2"/> Loading…</div>}
          {!loading && visible.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">
              <Megaphone size={28} className="mx-auto text-slate-300 mb-2" />
              No saved promote drafts yet. Drafts are saved automatically every time you generate one from a topic chip.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {visible.map((d) => (
              <li key={d.id} className="p-3 md:p-4 flex flex-col md:flex-row md:items-start gap-2 md:gap-3 hover:bg-slate-50" data-testid={`promote-history-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white"
                          style={{ backgroundColor: TOPIC_COLORS[d.topic] || "#334155" }}>
                      {d.topic}
                    </span>
                    {d.is_revision && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-sm">
                        Revision
                      </span>
                    )}
                    {d.area && <span className="text-[10px] text-slate-500">📍 {d.area}</span>}
                    {d.criteria && <span className="text-[10px] text-slate-500">· {d.criteria}</span>}
                    <span className="text-[10px] text-slate-400 ml-auto">{new Date(d.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm font-bold text-slate-900 truncate">{d.subject}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-line">{d.body}</div>
                  {d.feedback && (
                    <div className="mt-1 text-[10px] text-slate-600 italic">{tr("assistant.promote_feedback_previous", "Feedback on previous draft:")} "{d.feedback}"</div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-0.5">by {d.created_by}</div>
                </div>
                <div className="flex md:flex-col items-stretch gap-1.5 shrink-0">
                  <button onClick={() => onClone(d)}
                    data-testid={`promote-clone-${d.id}`}
                    className="inline-flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-blue-700 bg-blue-700 text-white hover:bg-blue-800"
                    title={tr("assistant.tip_clone_draft", "Clone & tweak this draft in the Promote panel")}>
                    <Copy size={11} weight="bold" /> Clone & tweak
                  </button>
                  <button onClick={() => copyToClipboard(`Subject: ${d.subject}\n\n${d.body}`)}
                    data-testid={`promote-copy-${d.id}`}
                    className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-slate-300 text-slate-700"
                    title={tr("assistant.tip_copy_email", "Copy subject + body to clipboard")}>
                    <Copy size={11} weight="regular" /> Copy
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


function PromotePanel({ topic, email, onClose, initialDraft }) {
  const tr = useT();
  const [step, setStep] = useState(initialDraft ? "draft" : "area");  // area | draft
  const [prepareOnly, setPrepareOnly] = useState(!!initialDraft);
  const [area, setArea] = useState(initialDraft?.area || "");
  const [criteria, setCriteria] = useState(initialDraft?.criteria || "");
  const [recipients, setRecipients] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [draft, setDraft] = useState(initialDraft ? { subject: initialDraft.subject, body: initialDraft.body } : null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [err, setErr] = useState("");

  // Initial area suggestion + recipient preview on mount (skip in clone mode).
  useEffect(() => {
    if (initialDraft) return;
    let alive = true;
    setRecipientsLoading(true);
    fetchAiPromoteRecipients({ requester_email: email, topic })
      .then(r => {
        if (!alive) return;
        setRecipients(r);
        if (!area && r.suggested_area) setArea(r.suggested_area);
      })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Couldn't load recipient preview"); })
      .finally(() => { if (alive) setRecipientsLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const refreshRecipients = async () => {
    setRecipientsLoading(true); setErr("");
    try {
      const r = await fetchAiPromoteRecipients({ requester_email: email, topic, area, criteria });
      setRecipients(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Search failed");
    } finally {
      setRecipientsLoading(false);
    }
  };

  const runDraft = async (useFeedback = false) => {
    setDraftLoading(true); setErr("");
    try {
      const payload = { requester_email: email, topic, area, criteria };
      if (useFeedback && feedback.trim() && draft) {
        payload.feedback = feedback.trim();
        payload.previous_draft = { subject: draft.subject, body: draft.body };
      }
      const d = await fetchAiPromoteDraft(payload);
      setDraft(d);
      if (useFeedback) setFeedback("");
      setStep("draft");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Draft failed");
    } finally { setDraftLoading(false); }
  };

  const openInMailApp = () => {
    if (!draft) return;
    // mailto: with To deliberately empty — user manually enters the recipient list
    // (or pastes from the sample shown in-app).
    const url = `mailto:?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
    window.location.href = url;
  };

  const color = TOPIC_COLORS[topic] || TOPIC_COLORS.other;

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-950/70 flex items-center justify-center p-3" onClick={onClose} data-testid="promote-panel">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
            <Megaphone size={18} weight="bold" style={{ color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo']">
              Promote <span className="capitalize" style={{ color }}>{topic}</span>
            </h3>
            <p className="text-[11px] text-slate-500">{tr("assistant.promote_intro", "Let me draft an outreach email & point you at the right customer segment.")}</p>
          </div>
          {/* Prepare-only toggle — hides the recipient search entirely */}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" title={tr("assistant.tip_skip_recipients", "Skip recipient search and just draft the email.")}>
            <input type="checkbox" checked={prepareOnly} onChange={(e) => setPrepareOnly(e.target.checked)}
              data-testid="promote-prepare-only"
              className="w-3 h-3" />
            Prepare email only
          </label>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={16}/></button>
        </header>

        {err && <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-200" data-testid="promote-error">{err}</div>}

        {/* STEP 1 — AREA & RECIPIENTS */}
        {!prepareOnly && step === "area" && (
          <div className="p-4 space-y-3 overflow-y-auto">
            <div className="bg-blue-50/50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-slate-700">
              <MagicWand size={13} weight="bold" className="inline text-blue-700 mr-1" />
              Based on recent leads in this topic, I recommend targeting{" "}
              <b data-testid="promote-suggested-area">{recipients?.suggested_area || "USA"}</b>.{" "}
              Edit the area below or add extra criteria if you want a different audience.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Region</label>
                <input value={area} onChange={(e) => setArea(e.target.value)} onBlur={refreshRecipients}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); refreshRecipients(); } }}
                  data-testid="promote-area"
                  placeholder={tr("assistant.recipient_search_placeholder", "USA, Canada, California, Ontario, Texas, 60*…")}
                  className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                <p className="text-[10px] text-slate-400 mt-1">{tr("assistant.promote_country_hint", "Country, state/province, or any text that appears in the billing address.")}</p>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">{tr("assistant.promote_extra_label", "Extra criteria (optional)")}</label>
                <input value={criteria} onChange={(e) => setCriteria(e.target.value)} onBlur={refreshRecipients}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); refreshRecipients(); } }}
                  data-testid="promote-criteria"
                  placeholder='Segment, industry, or note keyword — e.g. "paint", "fabrication"'
                  className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                <p className="text-[10px] text-slate-400 mt-1">{tr("assistant.promote_extra_hint", "Matches Company name, Segment, and Notes.")}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-[11px]">
                <UsersThree size={13} weight="bold" className="text-slate-500" />
                {recipientsLoading ? (
                  <span className="text-slate-400 inline-flex items-center gap-1"><Spinner size={10} className="animate-spin" /> Searching…</span>
                ) : (
                  <>
                    <b data-testid="promote-total">{recipients?.total_matches ?? 0}</b>
                    <span className="text-slate-500">{tr("assistant.promote_matching_count", "matching customers")}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-500">
                      USA {recipients?.breakdown?.USA ?? 0} · CA {recipients?.breakdown?.Canada ?? 0} · Other {recipients?.breakdown?.Other ?? 0}
                    </span>
                  </>
                )}
                <button onClick={refreshRecipients} disabled={recipientsLoading}
                  data-testid="promote-refresh"
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-blue-700 disabled:opacity-50">
                  <ArrowClockwise size={10} weight="bold" /> Refresh
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-slate-50 text-[11px]" data-testid="promote-sample-list">
                {(recipients?.sample || []).slice(0, 8).map((r, i) => (
                  <div key={i} className="px-3 py-1.5 flex gap-2">
                    <div className="flex-1 min-w-0 truncate">
                      <b className="text-slate-900">{r.company || "—"}</b>
                      <span className="text-slate-500"> · {r.person || "—"}</span>
                      <span className="text-slate-400"> · {r.email}</span>
                    </div>
                    <span className="shrink-0 text-[10px] text-slate-400 truncate max-w-[160px]">{r.address || ""}</span>
                  </div>
                ))}
                {!recipientsLoading && (recipients?.sample || []).length === 0 && (
                  <div className="px-3 py-4 text-center text-slate-400">{tr("assistant.promote_no_customers", "No customers matched. Broaden the area or remove extra criteria.")}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — DRAFT */}
        {step === "draft" && draft && (
          <div className="p-4 space-y-3 overflow-y-auto">
            <div className="text-[11px] text-slate-500">
              {tr("assistant.promote_drafted_for", "Drafted for")} <b>{topic}</b>{area ? <> · region <b>{area}</b></> : null}
              {!prepareOnly && recipients?.total_matches ? <> · audience ≈ <b>{recipients.total_matches}</b> customers</> : null}
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Subject</label>
              <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                data-testid="promote-draft-subject"
                className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Body</label>
              <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                data-testid="promote-draft-body"
                rows={9}
                className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono leading-relaxed" />
              <p className="text-[10px] text-slate-400 mt-1">{tr("assistant.promote_redo_hint", "You can edit freely, or give me feedback below and I'll redo it.")}</p>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5">
                <MagicWand size={11} weight="bold" className="text-blue-700" /> Tell me what to change
              </label>
              <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)}
                rows={2} data-testid="promote-feedback"
                placeholder='e.g. "Make it shorter.", "Reference winter service plan.", "More formal tone."'
                className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <button onClick={() => runDraft(true)} disabled={!feedback.trim() || draftLoading}
                data-testid="promote-redo"
                className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-50">
                {draftLoading ? <Spinner size={12} className="animate-spin" /> : <ArrowClockwise size={12} weight="bold" />}
                {tr("assistant.promote_redo_button", "Redo with my feedback")}
              </button>
            </div>

            {!prepareOnly && recipients?.sample?.length > 0 && (
              <details className="pt-2 border-t border-slate-100 text-[11px]">
                <summary className="cursor-pointer text-slate-600 font-semibold">
                  {tr("assistant.promote_recipients_hint", "Recipient emails — copy into your mail client's BCC field")} ({recipients.sample.length} shown)
                </summary>
                <div className="mt-2 font-mono text-[10px] bg-slate-50 p-2 rounded-sm max-h-40 overflow-y-auto break-all"
                     data-testid="promote-emails-list">
                  {recipients.sample.map(r => r.email).filter(Boolean).join(", ")}
                </div>
              </details>
            )}
          </div>
        )}

        {/* FOOTER */}
        <footer className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
          {step === "area" && (
            <>
              <div className="text-[11px] text-slate-500 flex-1">
                {prepareOnly
                  ? <>{tr("assistant.promote_prepare_only_hint", "Prepare-only mode: skip the recipient list and just draft the email.")}</>
                  : <>{tr("assistant.promote_audience_hint", "Happy with the audience? Draft the email next.")}</>}
              </div>
              <button onClick={() => runDraft(false)} disabled={draftLoading || (!prepareOnly && recipientsLoading)}
                data-testid="promote-draft-btn"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
                {draftLoading ? <Spinner size={12} className="animate-spin" /> : <MagicWand size={12} weight="bold" />}
                {tr("assistant.promote_draft_btn", "Draft email with AI")}
              </button>
            </>
          )}
          {step === "draft" && (
            <>
              <button onClick={() => setStep("area")}
                data-testid="promote-back"
                className="text-[11px] font-semibold text-slate-600 hover:text-blue-700">
                ← {tr("assistant.promote_back_edit_audience", "Edit audience")}
              </button>
              <div className="flex-1" />
              <button onClick={openInMailApp} data-testid="promote-open-mail"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
                <ArrowSquareOut size={12} weight="bold" /> Open in email app
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}


/** Light-weight markdown → React renderer. Handles headings, bullets,
 * numbered lists, inline bold/italic, and paragraph breaks. Deliberately
 * small — no markdown deps. */
function MarkdownView({ source }) {
  if (!source) return null;
  const lines = source.split("\n");
  const blocks = [];
  let paragraph = [];
  let inList = null; // 'ul' | 'ol' | null
  let listItems = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(" ");
    blocks.push({ type: "p", text });
    paragraph = [];
  };
  const flushList = () => {
    if (!inList) return;
    blocks.push({ type: inList, items: listItems });
    inList = null; listItems = [];
  };

  for (let raw of lines) {
    const ln = raw.replace(/\t/g, "  ");
    if (/^#\s/.test(ln))      { flushParagraph(); flushList(); blocks.push({ type: "h1", text: ln.replace(/^#\s+/, "") }); continue; }
    if (/^##\s/.test(ln))     { flushParagraph(); flushList(); blocks.push({ type: "h2", text: ln.replace(/^##\s+/, "") }); continue; }
    if (/^###\s/.test(ln))    { flushParagraph(); flushList(); blocks.push({ type: "h3", text: ln.replace(/^###\s+/, "") }); continue; }
    if (/^[-*]\s/.test(ln))   { flushParagraph(); if (inList !== "ul") { flushList(); inList = "ul"; }
                                listItems.push(ln.replace(/^[-*]\s+/, "")); continue; }
    if (/^\d+\.\s/.test(ln))  { flushParagraph(); if (inList !== "ol") { flushList(); inList = "ol"; }
                                listItems.push(ln.replace(/^\d+\.\s+/, "")); continue; }
    if (ln.trim() === "")     { flushParagraph(); flushList(); continue; }
    flushList();
    paragraph.push(ln);
  }
  flushParagraph(); flushList();

  const renderInline = (text) => {
    // Super-simple inline: **bold**, *italic*, `code`
    const parts = [];
    let rest = text;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/;
    let key = 0;
    while (rest.length) {
      const m = rest.match(re);
      if (!m) { parts.push(rest); break; }
      const idx = m.index;
      if (idx > 0) parts.push(rest.slice(0, idx));
      const token = m[0];
      if (token.startsWith("**")) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
      else if (token.startsWith("`")) parts.push(<code key={key++} className="bg-slate-100 text-slate-800 px-1 rounded-sm text-[11px]">{token.slice(1, -1)}</code>);
      else parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
      rest = rest.slice(idx + token.length);
    }
    return parts;
  };

  return (
    <article className="prose prose-sm max-w-none text-slate-800" data-testid="help-manual-content">
      {blocks.map((b, i) => {
        if (b.type === "h1") return <h1 key={i} className="text-xl font-bold font-['Chivo'] mt-1 mb-2 text-slate-900">{renderInline(b.text)}</h1>;
        if (b.type === "h2") return <h2 key={i} className="text-base font-bold font-['Chivo'] mt-4 mb-1 text-slate-900 border-b border-slate-100 pb-1">{renderInline(b.text)}</h2>;
        if (b.type === "h3") return <h3 key={i} className="text-sm font-bold mt-3 mb-1 text-slate-800">{renderInline(b.text)}</h3>;
        if (b.type === "ul") return <ul key={i} className="list-disc list-outside ml-5 mb-2 space-y-0.5 text-sm">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
        if (b.type === "ol") return <ol key={i} className="list-decimal list-outside ml-5 mb-2 space-y-0.5 text-sm">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>;
        return <p key={i} className="text-sm my-1.5 leading-relaxed">{renderInline(b.text)}</p>;
      })}
    </article>
  );
}


/** Role-adapted user manual with in-app reader and download menu. Admins see
 * a role picker (Guest / Customer / Admin) so they can read and download every
 * manual; non-admins are locked to their own. */
function HelpPanel({ user, isAdmin, onClose }) {
  const ownRole = isAdmin ? "admin" : (user?.role === "customer" ? "customer" : "guest");
  const [role, setRole] = useState(ownRole);
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dlOpen, setDlOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    fetchAiManual(role, user?.email || "")
      .then(m => { if (alive) setManual(m); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Couldn't load manual"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [role, user?.email]);

  const download = (format) => {
    const url = aiManualDownloadUrl(role, user?.email || "", format);
    // Open in a new tab so the Save dialog always appears reliably, even
    // on mobile Safari (direct navigation is blocked for attachment headers).
    window.open(url, "_blank", "noopener");
    setDlOpen(false);
  };

  const roleTabs = isAdmin
    ? [["guest", "Guest"], ["customer", "Customer"], ["admin", "Admin"]]
    : [[ownRole, ownRole.charAt(0).toUpperCase() + ownRole.slice(1)]];

  return (
    <div className="fixed inset-0 z-[1100] bg-slate-950/60 flex items-center justify-center p-3" onClick={onClose} data-testid="help-panel">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <Info size={20} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo']">AI Assistant — User manual</h3>
            <p className="text-[11px] text-slate-500">
              {isAdmin
                ? "You can read & download the manual for any role."
                : "Learn what the assistant can do for you and how to get the best answers."}
            </p>
          </div>
          {/* Role picker — admin only surfaces all three */}
          <div className="flex rounded-sm border border-slate-200 overflow-hidden text-[10px] font-bold uppercase tracking-wider" data-testid="help-role-tabs">
            {roleTabs.map(([k, label]) => (
              <button key={k} onClick={() => setRole(k)}
                data-testid={`help-role-${k}`}
                className={`px-2.5 py-1 ${role === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
          {/* Download dropdown */}
          <div className="relative">
            <button onClick={() => setDlOpen(v => !v)}
              data-testid="help-download-btn"
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm bg-blue-700 text-white hover:bg-blue-800">
              <DownloadSimple size={12} weight="bold" /> Download
              <CaretDown size={9} weight="bold" />
            </button>
            {dlOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-sm shadow-lg z-10 py-1 text-[11px]" data-testid="help-download-menu">
                <button onClick={() => download("pdf")} data-testid="help-download-pdf"
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 text-slate-700">
                  <FilePdf size={13} weight="bold" className="text-red-600" /> PDF (print-ready)
                </button>
                <button onClick={() => download("md")} data-testid="help-download-md"
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 text-slate-700">
                  <FileCode size={13} weight="bold" className="text-blue-600" /> Markdown (.md)
                </button>
                <button onClick={() => download("txt")} data-testid="help-download-txt"
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 text-slate-700">
                  <FileText size={13} weight="bold" className="text-slate-600" /> Plain text (.txt)
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={16}/></button>
        </header>

        <div className="flex-1 overflow-y-auto p-5" data-testid="help-manual-body">
          {loading && <div className="text-slate-400 text-sm inline-flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading manual…</div>}
          {err && <div className="text-red-600 text-sm" data-testid="help-manual-error">{err}</div>}
          {!loading && !err && manual && <MarkdownView source={manual.markdown} />}
        </div>

        <footer className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 flex items-center justify-between">
          <span>Model: <b>gpt-5.2</b> · Manuals are updated with every release.</span>
          {isAdmin && <span className="text-slate-400">Admin view — all manuals accessible</span>}
        </footer>
      </div>
    </div>
  );
}

function LeadsPanel({ user, onClose, onOpenSession }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all"); // all | new | contacted
  const [busyId, setBusyId] = useState(null);
  const [digest, setDigest] = useState(null);      // { subject, body, counts, last_sent, recipients }
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  const load = () => {
    setLoading(true);
    listAiLeads(user.email).then(d => {
      setItems(d.items || []);
      setTotal(d.total || 0);
    }).finally(() => setLoading(false));
    previewAiDigest(user.email).then(setDigest).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleContacted = async (id) => {
    setBusyId(id);
    try {
      await markAiLeadContacted(id, user.email);
      load();
    } finally { setBusyId(null); }
  };

  const sendDigestNow = async () => {
    const recipCount = digest?.recipients?.length || 0;
    const confirmMsg = `Send this weekly digest now to ${recipCount} admin recipient(s)?`;
    if (!window.confirm(confirmMsg)) return;
    setDigestBusy(true); setDigestMsg("");
    try {
      const r = await sendAiDigestNow(user.email);
      setDigestMsg(`Digest sent to ${r.recipients?.length || 0} admin(s).`);
      previewAiDigest(user.email).then(setDigest).catch(() => {});
    } catch (e) {
      setDigestMsg(e?.response?.data?.detail || "Failed to send digest.");
    } finally { setDigestBusy(false); }
  };

  const visible = items.filter(it => {
    if (filter === "new") return !it.contacted_at;
    if (filter === "contacted") return !!it.contacted_at;
    return true;
  });

  const newCount = items.filter(i => !i.contacted_at).length;

  return (
    <div className="fixed inset-0 z-[1100] bg-slate-950/60 flex items-center justify-center p-3" onClick={onClose} data-testid="leads-panel">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <UsersThree size={20} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo']">AI-Assistant Leads</h3>
            <p className="text-[11px] text-slate-500">
              {total} total · {newCount} unconverted · captured from "Email me this recommendation"
            </p>
          </div>
          <div className="flex rounded-sm border border-slate-200 text-[10px] font-bold uppercase tracking-wider overflow-hidden" data-testid="leads-filter">
            {[["all", "All"], ["new", "New"], ["contacted", "Done"]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                data-testid={`leads-filter-${k}`}
                className={`px-2.5 py-1 ${filter === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><X size={16}/></button>
        </header>

        {/* Weekly digest banner */}
        {digest && (
          <div className="px-4 py-2.5 border-b border-slate-200 bg-blue-50/40 flex flex-col md:flex-row md:items-center gap-2" data-testid="leads-digest-banner">
            <div className="flex-1 min-w-0 text-[11px] text-slate-700">
              <div className="font-bold text-slate-900 flex items-center gap-1.5">
                <PaperPlaneTilt size={13} weight="bold" className="text-blue-700" />
                Weekly digest <span className="text-slate-500 font-normal">· {digest.counts?.new_leads} new, {digest.counts?.unconverted} unconverted, {digest.counts?.chats_this_week} chats in 7d</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {digest.last_sent?.sent_at
                  ? <>Last sent {new Date(digest.last_sent.sent_at).toLocaleString()} · auto-runs every <b>Monday 09:00 UTC</b> to {digest.recipients?.length || 0} admin(s)</>
                  : <>Never sent · auto-runs every <b>Monday 09:00 UTC</b> to {digest.recipients?.length || 0} admin(s)</>}
              </div>
              {digestMsg && <div className="text-[10px] text-emerald-700 font-semibold mt-0.5" data-testid="leads-digest-msg">{digestMsg}</div>}
            </div>
            <button onClick={sendDigestNow} disabled={digestBusy}
              data-testid="leads-digest-send"
              className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
              {digestBusy ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
              Send digest now
            </button>
          </div>
        )}

        {/* Stacked topic trends with range + bucket picker */}
        <TopicChart email={user.email} />

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-400"><Spinner size={14} className="animate-spin inline mr-2"/> Loading…</div>}
          {!loading && visible.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">
              <EnvelopeSimple size={28} className="mx-auto text-slate-300 mb-2" />
              {filter === "new" ? "No new leads right now." : filter === "contacted" ? "No contacted leads yet." : "No leads captured yet."}
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {visible.map((it) => {
              const done = !!it.contacted_at;
              return (
                <li key={it.id} className="p-3 md:p-4 flex flex-col md:flex-row md:items-start gap-2 md:gap-3 hover:bg-slate-50" data-testid={`lead-row-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                        {done ? "Contacted" : "New"}
                      </span>
                      <span className="text-[10px] text-slate-400">{new Date(it.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5 break-all">
                      <EnvelopeSimple size={13} weight="bold" className="text-blue-700 shrink-0" />
                      <a href={`mailto:${it.to_email}`} className="hover:underline" data-testid={`lead-email-${it.id}`}>{it.to_email}</a>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      <span className="font-semibold">Topic:</span> {it.session_title || "(untitled)"} · {it.message_count} msgs
                    </div>
                    {it.note && (
                      <div className="mt-1.5 text-[11px] text-slate-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 italic">"{it.note}"</div>
                    )}
                    {it.contacted_at && (
                      <div className="mt-1 text-[10px] text-emerald-600">Marked done by {it.contacted_by} · {new Date(it.contacted_at).toLocaleString()}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => onOpenSession(it.session_id)}
                      data-testid={`lead-open-${it.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700"
                      title="Open the full conversation">
                      <ChatCircle size={12} weight="bold" /> View chat
                    </button>
                    <button onClick={() => toggleContacted(it.id)}
                      disabled={busyId === it.id}
                      data-testid={`lead-toggle-${it.id}`}
                      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border disabled:opacity-50 ${done ? "border-slate-200 bg-white hover:border-amber-400 hover:text-amber-700 text-slate-700" : "border-emerald-700 bg-emerald-700 hover:bg-emerald-800 text-white"}`}
                      title={done ? "Mark as new" : "Mark as contacted"}>
                      {busyId === it.id ? <Spinner size={12} className="animate-spin" /> : (done ? <Phone size={12} weight="bold" /> : <CheckCircle size={12} weight="bold" />)}
                      {done ? "Re-open" : "Mark done"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** Floating chat shortcut. Mount once globally; navigates to /assistant. */
export function AssistantFab({ user }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useT();
  // iter150AZ.52 — Hide when any modal is open so we don't render two
  // floating "dialog circles" at once.
  const modalOpen = useIsModalOpen();
  // Available to every logged-in user (admins, customers, and guests).
  // Backend already enforces role-scoping on database tools.
  // Hide on the assistant page itself — there's nowhere to go.
  if (!user) return null;
  if (pathname?.startsWith("/assistant")) return null;
  if (modalOpen) return null;
  return (
    <button onClick={() => navigate("/assistant")} data-testid="assistant-fab"
      className="fixed left-4 md:left-5 z-[800] w-14 h-14 rounded-full bg-blue-700 hover:bg-blue-800 text-white shadow-xl flex items-center justify-center group"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
      title={t("ai.try_assistant", "AI Assistant")}>
      <ChatCircleDots size={24} weight="bold" />
      <span className="absolute left-16 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        {t("ai.try_assistant", "AI Assistant")}
      </span>
    </button>
  );
}

function ActionHistoryPanel({ user, onClose }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [windowSecs, setWindowSecs] = useState(60);

  const load = () => {
    setLoading(true);
    listAiActionHistory(user.email, 50)
      .then(d => {
        setItems(d.items || []);
        if (d.undo_window_seconds) setWindowSecs(d.undo_window_seconds);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Tick every second so the countdown badge updates.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const undo = async (it) => {
    setBusyId(it.id);
    try {
      const r = await undoAiAction(it.id, user.email);
      const { toast } = await import("sonner");
      toast.success(t("ai.history.undone", "Action undone."), { description: r.summary });
      load();
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(t("ai.history.undo_failed", "Could not undo this action."),
        { description: e?.response?.data?.detail || "" });
    } finally {
      setBusyId(null);
    }
  };

  const STATUS_BADGE = {
    AI_ACTION_EXECUTED:  { label: t("ai.history.executed", "executed"),  cls: "bg-emerald-100 text-emerald-800" },
    AI_ACTION_PROPOSED:  { label: t("ai.history.proposed", "proposed"),  cls: "bg-amber-100 text-amber-800" },
    AI_ACTION_CANCELLED: { label: t("ai.history.cancelled", "cancelled"), cls: "bg-slate-200 text-slate-700" },
    AI_ACTION_FAILED:    { label: t("ai.history.failed", "failed"),     cls: "bg-red-100 text-red-700" },
    AI_ACTION_UNDONE:    { label: "undone",                              cls: "bg-purple-100 text-purple-800" },
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-slate-950/60 flex items-center justify-center p-3" onClick={onClose} data-testid="action-history-panel">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="shrink-0 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-bold text-base inline-flex items-center gap-2">
            <ClockCounterClockwise size={16} weight="bold" /> {t("ai.history.title", "Action history")}
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} data-testid="action-history-refresh"
              className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
              <ArrowClockwise size={11} weight="bold" className="inline -mt-0.5" /> {t("ai.history.refresh", "Refresh")}
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100" title={t("assistant.tip_close", "Close")} data-testid="action-history-close">
              <X size={14} />
            </button>
          </div>
        </header>
        <p className="px-4 py-2 text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50">
          {t("ai.history.window_hint", "Undo is available for 60 seconds after execution.")}
        </p>
        <div className="flex-1 overflow-y-auto p-3">
          {loading && <div className="text-slate-400 text-sm flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-slate-400 text-sm text-center py-12">{t("ai.history.empty", "No actions executed yet.")}</div>
          )}
          {!loading && items.length > 0 && (
            <ul className="space-y-2">
              {items.map((it) => {
                const badge = STATUS_BADGE[it.action_type] || { label: it.action_type, cls: "bg-slate-100 text-slate-600" };
                // Compute live countdown (recompute relative to wall clock so it ticks).
                const tsMs = it.ts ? Date.parse(it.ts) : 0;
                const elapsedLive = tsMs ? Math.floor((now - tsMs) / 1000) : 9999;
                const remainingLive = Math.max(0, windowSecs - elapsedLive);
                const stillUndoable = it.undoable && remainingLive > 0;
                return (
                  <li key={it.id} className="border border-slate-200 rounded-sm p-2.5 text-xs hover:border-slate-300" data-testid={`action-history-row-${it.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${badge.cls}`}>
                        {badge.label}
                      </span>
                      <code className="text-[11px] font-bold text-slate-900">{it.tool_name || "—"}</code>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500" title={it.ts}>{new Date(it.ts).toLocaleString()}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500 truncate">{it.actor_email}</span>
                      {stillUndoable && (
                        <span className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-sm">
                          {remainingLive}s
                        </span>
                      )}
                    </div>
                    {it.entity_label && (
                      <div className="mt-1 text-slate-700 truncate">{it.entity_label}</div>
                    )}
                    {Object.keys(it.params || {}).length > 0 && (
                      <details className="mt-1">
                        <summary className="text-[10px] uppercase tracking-wider text-slate-400 cursor-pointer hover:text-slate-700">params</summary>
                        <pre className="mt-1 bg-slate-50 border border-slate-100 p-1.5 text-[10px] whitespace-pre-wrap break-all">{JSON.stringify(it.params, null, 2)}</pre>
                      </details>
                    )}
                    {stillUndoable && (
                      <div className="mt-2">
                        <button onClick={() => undo(it)} disabled={busyId === it.id}
                          data-testid={`action-history-undo-${it.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-sm bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50">
                          {busyId === it.id ? <Spinner size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />}
                          {t("ai.history.undo", "Undo")}
                        </button>
                      </div>
                    )}
                    {it.already_undone && (
                      <div className="mt-1 text-[10px] text-purple-600 font-bold uppercase tracking-wider">undone</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}



/**
 * SendPublicFilesConfirmCard — per-file approval UI for the
 * ``send_public_files`` AI action. Renders each resolved file with a
 * checkbox (all checked by default), shows the recipient list and
 * optional note, then sends back a stripped-down ``file_ids`` list when
 * the user clicks Confirm. Replaces the plain yes/no confirm row only
 * for this specific action.
 */
function SendPublicFilesConfirmCard({ preview, params, onSend, onCancel, disabled, t }) {
  const initialIds = (preview?.files || []).map((f) => f.id);
  const [approved, setApproved] = useState(() => new Set(initialIds));
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplScope, setTplScope] = useState("personal");
  const [tplBusy, setTplBusy] = useState(false);
  const [tplSavedName, setTplSavedName] = useState("");
  const recipients = preview?.recipients || [];
  const note = preview?.note || "";
  const matchedTemplate = preview?.matched_template;

  const toggle = (id) => {
    setApproved((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const approvedCount = approved.size;
  const canSend = approvedCount > 0 && recipients.length > 0 && !disabled;

  if (!preview?.files?.length) {
    return (
      <div
        data-testid="assistant-send-files-empty"
        className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-800 bg-amber-50 rounded-sm p-2.5"
      >
        <b>{t("assistant.no_files_found", "No matching files found.")}</b> Try rephrasing — e.g. include the
        product family ("screw compressor", "air dryer", "oil-free"), or
        the document kind ("catalogue", "brochure", "manual", "warranty").
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={onCancel}
            data-testid="assistant-action-cancel"
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700"
          >
            <X size={10} weight="bold" /> {t("ai.action.cancel", "Cancel")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="assistant-send-files-confirm-card"
      className="mt-3 pt-3 border-t border-blue-100 space-y-2.5"
    >
      {/* Recipients */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">
          {t("ai.action.recipients", "Recipients")}
          <span className="ml-1 text-slate-400 font-normal normal-case tracking-normal">
            · {recipients.length}
          </span>
        </div>
        {recipients.length === 0 ? (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
            {t("assistant.no_recipients_full", "No recipients resolved — add a person or company to your request (e.g. \"send … to Link Wu at Fitzpatrick Trailers\").")}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {recipients.map((r) => (
              <span
                key={r}
                data-testid={`assistant-send-files-recipient-${r}`}
                className="inline-flex items-center text-[11px] font-mono text-slate-700 bg-white border border-slate-200 rounded-sm px-1.5 h-5"
              >
                {r}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Per-file approval list */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">
          {t("ai.action.approve_files", "Approve each file")}
          <span className="ml-1 text-slate-400 font-normal normal-case tracking-normal">
            · {approvedCount}/{preview.files.length} selected
          </span>
        </div>
        <ul className="space-y-1">
          {preview.files.map((f) => {
            const checked = approved.has(f.id);
            return (
              <li
                key={f.id}
                data-testid={`assistant-send-files-row-${f.id}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded-sm border cursor-pointer transition-colors ${
                  checked
                    ? "bg-blue-50 border-blue-200"
                    : "border-slate-200 hover:bg-slate-50 opacity-60"
                }`}
                onClick={() => toggle(f.id)}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(f.id)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`assistant-send-files-checkbox-${f.id}`}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">
                    {f.file_type}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {f.file_class}
                    {f.explanation ? ` — ${f.explanation}` : ""}
                  </div>
                  {!f.available_for_customer && (
                    <span className="inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1 py-0.5 rounded-sm">
                      {t("assistant.send_files_internal_only", "Internal only")}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Matched template badge */}
      {matchedTemplate?.id && (
        <div
          data-testid="assistant-send-files-matched-template"
          className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1 inline-flex items-center gap-1"
        >
          <BookmarkSimple size={10} weight="bold" />
          Using template: <b>{matchedTemplate.name}</b>
        </div>
      )}

      {/* Note preview */}
      {note && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5 text-[11px] text-slate-600">
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mr-1">
            {t("ai.action.note_label", "Note")}
          </span>
          {note}
        </div>
      )}

      {/* Save-as-template (only if not already a template-driven send) */}
      {!matchedTemplate?.id && approvedCount > 0 && !tplSavedName && (
        savingTpl ? (
          <form
            data-testid="assistant-send-files-save-tpl-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const name = tplName.trim();
              if (!name) return;
              setTplBusy(true);
              try {
                await createPublicFileTemplate({
                  name,
                  file_ids: Array.from(approved),
                  scope: tplScope,
                });
                setTplSavedName(name);
                setSavingTpl(false);
              } catch (err) { /* swallow */ }
              finally { setTplBusy(false); }
            }}
            className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1"
          >
            <input
              autoFocus
              data-testid="assistant-send-files-tpl-name"
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              placeholder={t("assistant.template_name_placeholder", "Template name (e.g. Discovery pack)")}
              className="h-6 px-1.5 text-[11px] rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 w-48"
            />
            <select
              data-testid="assistant-send-files-tpl-scope"
              value={tplScope}
              onChange={(e) => setTplScope(e.target.value)}
              className="h-6 px-1 text-[10px] rounded-sm border border-slate-300 bg-white"
            >
              <option value="personal">{t("assistant.template_just_me", "Just me")}</option>
              <option value="shared">{t("assistant.template_shared", "Shared with team")}</option>
            </select>
            <button
              type="submit"
              data-testid="assistant-send-files-tpl-save"
              disabled={tplBusy || !tplName.trim()}
              className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-300"
            >
              {tplBusy ? "…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setSavingTpl(false); setTplName(""); }}
              className="text-slate-400 hover:text-slate-700 w-5 h-5 flex items-center justify-center"
            >
              <X size={10} />
            </button>
          </form>
        ) : (
          <button
            data-testid="assistant-send-files-save-tpl-btn"
            onClick={() => setSavingTpl(true)}
            className="inline-flex items-center gap-1 h-6 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-white border border-violet-200 text-violet-700 hover:bg-violet-50"
          >
            <FloppyDisk size={10} weight="bold" /> Save as template…
          </button>
        )
      )}
      {tplSavedName && (
        <div
          data-testid="assistant-send-files-tpl-saved"
          className="text-[10px] text-emerald-700 inline-flex items-center gap-1"
        >
          <CheckCircle size={10} weight="bold" />
          Saved template "{tplSavedName}". Next time say <i>"send {tplSavedName} to …"</i>.
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => onSend(Array.from(approved), recipients)}
          disabled={!canSend}
          data-testid="assistant-send-files-confirm"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300"
        >
          <CheckCircle size={12} weight="bold" />
          {t("ai.action.send_selected", "Send")}
          &nbsp;{approvedCount}
          &nbsp;{approvedCount === 1 ? t("ai.action.file_singular", "file") : t("ai.action.file_plural", "files")}
        </button>
        <button
          onClick={onCancel}
          disabled={disabled}
          data-testid="assistant-action-cancel"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
        >
          <X size={12} weight="bold" /> {t("ai.action.cancel", "Cancel")}
        </button>
        <span className="text-[10px] text-slate-400 normal-case tracking-normal">
          {t("ai.action.tool_label", "tool")}: <b>send_public_files</b>
        </span>
      </div>
    </div>
  );
}
